import { t_process_state, IProcess, IProcessDelegate, TProcessConfiguration } from '../resources/process';

import $ip, { t_locator } from'../../../pip/library/pi-ip/pi-ip.js';
import ess from'../../../pip/library/pi-ess/pi-ess.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

/**
 * This is the middleware process configuration which should be attacked to the
 * Process Hub configuration if it uses Middleware
 */
export interface IMiddlewareConfiguration extends TProcessConfiguration {
  interceptors: {
    configuration: string[];
  };
}

/**
 * When a middleware script is loaded it should provide an object with a run function
 *
 * ```
 * export async function run(configuration) {
 *   return configuration;
 * }
 * ```
 */
export interface IMiddlewareScript {
  run(configuration: Record<string, TProcessConfiguration>): Promise<Record<string, TProcessConfiguration>>;
}

export type TServiceConfigurations = Record<string, TProcessConfiguration>;
export type TServiceMemory = Record<string, Object>;

/**
 * Every Process Hub implements `IKernel` but generally it is not needed as you will
 * create your own Kernel and can interact with it directly.
 *
 * This defines the public interface for a `Kernel`
 */
export interface IKernel<ProcessDelegates = {}> {
  /**
   * After `startup` this record will be loaded with delegates from all registered process
   */
  readonly processes: ProcessDelegates;

  /**
   * overrides the existing configuration. typically we expect
   * a configuration to be assigned once, and should be
   * able to be replaced; so if you want to deep merge, with the
   * existing configuration you have to do it yourself
   */
  configure<ProcessConfigurations extends TServiceConfigurations>(
    configuration: ProcessConfigurations
  ): ProcessConfigurations;

  /**
   * register a single process
   */
  register(process: IProcess): this;

  /**
   * turns on process on
   */
  startup<ProcessConfigurations extends TServiceConfigurations, SharedMemory extends TServiceMemory>(
    configuration: ProcessConfigurations,
    sharedMemory?: SharedMemory
  ): Promise<IProcess[]>;

  /**
   * turns all process off
   */
  shutdown(): Promise<IProcess[]>;
}

/**
 * A Process Hub manages a set of Process. The Process Hub is usually linked to a Product
 * or sub-system within a Product.
 *
 * A Process Hub needs all Process registered before `startup`; in other words, all Process are statically
 * linked, at present.
 *
 * Your hub should extend or create a Kernel which stores and manages
 * Process. The Process are turned on and can be turned off by the
 * Hub.
 *
 * The Hub requires a configuration as a Dictionary where the name of each
 * Process matches a key in the Dictionary.
 *
 * @example
 *
 * ```typescript
 *  const processHub = new Kernel();
 *  processHub.register(new ProcessFirst());
 *
 *  (async function() {
 *    // all process are initialized and activated
 *    await processHub.startup({
 *      first: {
 *        disabled: false
 *      }
 *    });
 *
 *    // this is either the process itself or the delegate
 *    processHub.process.first;
 *
 *    // all process are deactivated and uninitialized
 *    await processHub.shutdown();
 *  })();
 * ```
 *
 */
export class Kernel<ProcessDelegates extends Record<t_locator, IProcessDelegate> = {}>
  implements IKernel<ProcessDelegates> {
  readonly id: string;

  /**
   * all known process, not all may be active
   */
  private internal: Map<t_locator, IProcess> = new Map();

  /**
   * configuration objects for every process. the key
   * of the dictionary is the name of each process.
   */
  private configuration: TServiceConfigurations = {} as TServiceConfigurations;

  /**
   *
   */
  private memory: TServiceMemory = {} as TServiceMemory;

  /**
   * all process registered to the process hub
   */
  processes: ProcessDelegates = {} as ProcessDelegates;

  /**
   * creates instance of Kernel
   */
  static make(id: string) {
    return new Kernel(id);
  }

  /**
   * every process hub requires a namespace which should be the same as all process registered with it
   *
   * @param dependencies you can assign dependencies initially when creating the Kernel
   */
  constructor(id: string) {
    this.id = id;
  }

  /**
   * overrides the existing configuration. typically we expect a configuration to be
   * assigned once, and should be able to be replaced; so if you want to deep merge,
   * with the existing configuration you have to do it yourself
   */
  configure<ProcessConfigurations extends TServiceConfigurations>(configuration: ProcessConfigurations) {
    return (this.configuration = configuration);
  }

  /**
   *
   */
  share<SharedMemory extends TServiceMemory>(memory: SharedMemory) {
    return (this.memory = memory);
  }

  /**
   * register a single process internally
   */
  register(process: IProcess) {
    process.ns = this.id;

    this.internal.set(`/${process.id}`, process);
    return this;
  }

  /**
   * unregister a single process
   */
  unregister(process: t_locator | IProcess) {
    const processId = f1.is.string(process) ? process : (process as IProcess).id;

    this.internal.delete(`/${processId}`);
    return this;
  }

  /**
   * Runs the initialize and activate steps for all process. The steps are:
   *
   * 1. import resources for each process
   * 2. share memory with processes
   * 3. run `initialize` on all processes
   * 4. update the global configuration with changes from each Process
   * 5. apply middleware
   * 6. inject Process Delegates in to the Process Hub and each Process
   * 7. run `activate` on all Process
   */
  startup<ProcessConfigurations extends TServiceConfigurations, SharedMemory extends TServiceMemory>(
    configuration: ProcessConfigurations,
    memory: SharedMemory = {} as SharedMemory
  ) {
    this.configure(configuration);
    this.share(memory);

    const processes = this.__enabledProcess(this.configuration);
    return f1
      .serial(
        [
          this.__import.bind(this),
          this.__applySharedMemory.bind(this),
          this.__initialize.bind(this),
          this.__mergeConfiguration.bind(this),
          this.__applyMiddleware.bind(this),
          this.__injectDependencies.bind(this),
          this.__activate.bind(this)
        ],
        processes
      )
      .then(() => processes) as Promise<IProcess[]>;
  }

  /**
   * initialize all enabled process
   */
  private __import(process: IProcess[]) {
    return f1.parallel(
      f1.map(
        (process) => () => {
          ess.tap(process, {
            site: 'kernel',
            action: 'import-resources'
          });

          return $ip.requires(process.imports || []).then((resources) => {
            process.resources = resources;
            return process;
          });
        },
        process
      ),
      f1.skip
    );
  }

  /**
   * initialize all enabled process
   */
  private __initialize(process: IProcess[]) {
    return this.__run('initialize', process);
  }

  /**
   * upgrade the initialized configuration from each process back in to the
   * main configuration for the hub
   */
  private __mergeConfiguration(process: IProcess[]) {
    f1.each((process) => {
      (this.configuration as TServiceConfigurations)[process.id] = process.configuration =
        process.configuration ?? this.configuration[process.id];
    }, process);

    return f1.done(process);
  }

  /**
   * share memory with each process
   */
  private __applySharedMemory(process: IProcess[]) {
    f1.each((process) => {
      process.$memory(this.memory[process.id] || {});
    }, process);

    return f1.done(process);
  }

  /**
   * run all middleware functions and assign back to each process.
   * middleware/configuration is allowed to modify the process/hub
   * configuration
   */
  private __applyMiddleware(processes: IProcess[]) {
    // @todo we need to add middleware support to the process framework
    const configuration = this.configuration;
    const middleware: IMiddlewareConfiguration = configuration?.middleware as IMiddlewareConfiguration;
    const middlewareURIs = middleware?.interceptors?.configuration;

    return $ip
      .requires<IMiddlewareScript>(middlewareURIs)
      .then((interceptors) => {
        return f1.reduce(
          (config, interceptor, index) =>
            config.then((_config) => {
              ess.tap(
                {
                  id: middlewareURIs[index],
                  interceptor,
                  config
                },
                { site: 'kernel', action: 'apply-middleware' }
              );

              return f1.is.function(interceptor.run) ? interceptor.run(_config) : Promise.resolve(_config);
            }),
          Promise.resolve(configuration),
          interceptors
        );
      })
      .then((configuration) => {
        f1.each((process) => {
          const config = f1.safe({}, configuration[process.id]);
          process.configuration = f1.merge('over', process.configuration, config);
        }, processes);
      });
  }

  /**
   * after initialization a process will have all dependent process injected into the process.
   */
  private __injectDependencies(processes: IProcess[]) {
    this.processes = f1.reduce((processes, process) => this._injectProcess(processes, `/${process.id}`), {}, processes);

    f1.each((process) => {
      process.processes = f1.reduce(
        (process, dependency: t_locator) => this._injectProcess(process, dependency),
        {},
        process.dependencies
      );
    }, processes);

    return f1.done(null);
  }

  /**
   * inject a process or delegate in to the process record
   */
  protected _injectProcess(processes: Record<t_locator, IProcessDelegate>, processLocator: t_locator) {
    const process = this.internal.get(processLocator);
    if (process) {
      processes[process.id] = process.$delegate();
    }

    return processes;
  }

  /**
   * activate all enabled process
   */
  private __activate(process: IProcess[]) {
    return this.__run('activate', process);
  }

  /**
   * Runs the deactivate and uninitialize steps for all process. The steps are:
   *
   * 1. run `deactivate` on all Process
   * 2. uninject Process Delegates in to the Process Hub and each Process
   * 3. run `uninitialize` on all Process
   */
  shutdown() {
    const processes = this.__enabledProcess(this.configuration);
    return f1
      .serial(
        [
          this.__deactivate.bind(this),
          this.__uninjectDependencies.bind(this),
          this.__uninitialize.bind(this),
          this.__unimport.bind(this)
        ],
        processes
      )
      .then(() => processes) as Promise<IProcess[]>;
  }

  /**
   * deactivate all enabled process
   */
  private __deactivate(process: IProcess[]) {
    return this.__run('deactivate', process);
  }

  /**
   * After deactivation, we empty all process from the Kernel and all supporting Process
   */
  private __uninjectDependencies(process: IProcess[]) {
    this.processes = {} as ProcessDelegates;

    f1.each((process) => {
      process.processes = {};
    }, process);

    return f1.done(null);
  }

  /**
   * uninitialize all enabled process
   */
  private __uninitialize(process: IProcess[]) {
    return this.__run('uninitialize', process);
  }

  /**
   * unloads all resources which were loaded
   */
  private __unimport(process: IProcess[]) {
    return f1.parallel(
      f1.map((process) => {
        return () => $ip.destroy(process.resources || []).then(() => process);
      }, process),
      process
    );
  }

  /**
   * @returns enabled process
   */
  private __enabledProcess(configuration: Record<string, TProcessConfiguration>) {
    return f1.filter((process) => {
      return f1.not(f1.skip, f1.read(f1.lens(`/${process.id}/disabled`), false, configuration));
    }, Array.from(this.internal.values()));
  }

  /**
   * run a life-cycle method on all enabled process and monitor the success/failure of each
   *
   * this normalizes the steps applied to each process across the four life-cycle methods:
   * - initialize
   * - activate
   * - deactivate
   * - uninitialize
   */
  private __run(action: string, process: IProcess[]) {
    const has = f1.filter(f1.compose(f1.is.function, f1.read(f1.lens(`/${action}`), null)));

    return Promise.all(
      f1.map((process) => {
        const result = process[action]
          ? process[action](f1.read(f1.lens(`/${process.id}`), {}, this.configuration), process.resources)
          : f1.done(this);

        return this.__monitor(process, action, result);
      }, has(process) as IProcess[])
    );
  }

  /**
   * report on process hub activity through ESS
   */
  private __monitor<T>(process: IProcess, state: string, action: Promise<T | null>): Promise<T | null> {
    return action
      .then((v) => {
        process.state = `${state}` as t_process_state;
        ess.tap(process, {
          status: 'success',
          site: 'kernel',
          action: state
        });

        return v;
      })
      .catch((error: Error) => {
        process.state = `error`;
        ess.tap(process, {
          status: 'error',
          site: 'kernel',
          action: state,
          reason: `${error}`
        });

        return f1.done(null);
      });
  }
}
